import { Query, QueryStringParams } from '@/src/interfaces'
import { ReadonlyURLSearchParams } from 'next/navigation'

/**
 * Updates the URL search parameters based on the query and query parameters.
 *
 * @param {Query} query - The query object containing the query parameters.
 * @param {QueryStringParams} queryParams - The query string parameters to update.
 * @param {ReadonlyURLSearchParams} currentParams - The current URL search parameters.
 * @returns {void} Updates the URL search parameters.
 */
export const updateUrlSearchParameters = (
  query: Query,
  queryParams: QueryStringParams,
  currentParams: ReadonlyURLSearchParams
): void => {
  const searchParams = new URLSearchParams(currentParams.toString())

  const singleKeys = ['specialty', 'disease_state', 'unified', 'full_name']
  const checkboxKeys = [
    'booking_url',
    'telehealth_appointment',
    'accepts_new_patients',
  ]
  const locationKeys = ['state', 'city', 'zip']

  // Reset singleValue fields
  singleKeys.forEach((key) => {
    searchParams.delete(key)
  })

  if (query.singleValueParameters?.key && query.singleValueParameters?.value) {
    searchParams.set(
      query.singleValueParameters.key,
      query.singleValueParameters.value
    )
  }

  // Standard filters
  if (query.insuranceParameters)
    searchParams.set('insurance', query.insuranceParameters)
  else searchParams.delete('insurance')

  if (query.genderParameters) searchParams.set('gender', query.genderParameters)
  else searchParams.delete('gender')

  if (query.languageParameters)
    searchParams.set('language', query.languageParameters)
  else searchParams.delete('language')

  // Checkbox filters
  checkboxKeys.forEach((key) => {
    searchParams.delete(key)
    const value = query.checkBoxedParameters?.[key]
    if (value === true) searchParams.set(key, 'true')
  })

  // Location parameters
  locationKeys.forEach((key) => {
    searchParams.delete(key)
    const value =
      query.locationParameters?.[key as keyof typeof query.locationParameters]
    if (value) searchParams.set(key, value)
  })

  // Geo distance
  const geo = query.geoDistanceParameters
  if (geo?.distance && geo.currentLocation?.lat && geo.currentLocation?.lon) {
    searchParams.set('distance', geo.distance)
    searchParams.set('lat', geo.currentLocation.lat.toString())
    searchParams.set('lon', geo.currentLocation.lon.toString())
  } else {
    ;['distance', 'lat', 'lon'].forEach((key) => searchParams.delete(key))
  }

  // Additional queryParams
  for (const [key, value] of Object.entries(queryParams)) {
    searchParams.delete(key)
    if (value !== undefined && value !== '')
      searchParams.set(key, value.toString())
  }

  // Apply to browser URL
  window.history.replaceState({}, '', `?${searchParams.toString()}`)
}
